import { useState, useCallback, useEffect, useRef } from 'react';

import { message } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * useOrderAnalytics Hook
 * 處理訂單銷量分析的 API 調用和進度監聽
 *
 * @param {Object} options
 * @param {Function} options.apiFetch - API fetch 函數 (adminApiFetch)
 * @param {Function} options.addJob - 從 useAdminNotification 取得的 addJob 函數
 * @returns {Object} { loading, data, error, query, progress }
 */
export const useOrderAnalytics = ({ apiFetch, addJob }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const currentJobIdRef = useRef(null);

  /**
   * 執行查詢
   * @param {Object} params
   * @param {string} params.start_date - 開始日期 (YYYY-MM-DD)
   * @param {string} params.end_date - 結束日期 (YYYY-MM-DD)
   * @param {Array<number>} params.channel_ids - 通路 ID 陣列
   * @param {string} params.granularity - 時間粒度 ('daily', 'weekly', 'monthly')
   */
  const query = useCallback(async (params) => {
    try {
      setLoading(true);
      setError(null);
      setProgress(null);
      setData(null);

      // 調用 API 開始查詢
      const response = await apiFetch('orders/analytics.json', {
        method: 'POST',
        body: JSON.stringify(params),
      });

      // 解析 JSON response
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || t('common.error.unknown_error'));
      }

      const jobId = data.data.job_id;
      currentJobIdRef.current = jobId;

      // 添加背景任務到管理器，並設定 onComplete callback
      addJob(jobId, 'admin.pages.orders.analytics.title', (result) => {
        // 任務完成時的回調
        if (currentJobIdRef.current === jobId) {
          setLoading(false);
          setProgress(null);
          setData(result);
          currentJobIdRef.current = null;
        }
      });

    } catch (err) {
      setLoading(false);
      setProgress(null);
      setError(err.message);
      message.error(err.message);
      currentJobIdRef.current = null;
    }
  }, [apiFetch, addJob, t]);

  /**
   * 清理當前任務
   */
  useEffect(() => {
    return () => {
      currentJobIdRef.current = null;
    };
  }, []);

  return {
    loading,
    data,
    error,
    progress,
    query,
  };
};

export default useOrderAnalytics;
